import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { plannerTourBridge } from '../services/planner-tour-bridge';
// Die Selektoren lagen bis 25.08.2026 hier lokal. Sie stehen jetzt zentral,
// weil `mentor-endgame` dieselbe Zeile ankert und eine zweite Kopie
// auseinanderdriften wuerde — dort fehlte genau dieses Muster.
import {
  OPEN_REQ_ROW_SELECTOR,
  reattachOpenRequirementsAnchors,
} from '../config/planner-anchor-selectors';

/**
 * Kontextuelle `?`-Hilfe-Tour fuer die Schichtzuweisungs-Ansicht (Planner,
 * Route `/planner`). Pendant zur `STAFFING_BEDARFSPLAN_HELP_TOUR` des
 * Personalbedarfs — beide erklaeren ihren jeweiligen Schritt des
 * Planungs-Workflows (Schritt 1 Personalbedarf ↔ Schritt 2 Schichtzuweisung).
 *
 * Waehrend die grosse `SHIFT_PLANNING_ONBOARDING_TOUR` den Setup-Funnel
 * (Schicht anlegen → Toolbar → Handoff) abdeckt, erklaert diese kleine Tour
 * die *ganze Ansicht zum Nachschlagen*: Wochenliste, Mitarbeiterspalte,
 * offene Anforderungen, Kalender-Kacheln, die primaeren Toolbar-Aktionen,
 * das Drei-Punkte-Menue und das Freigeben an die Mitarbeiter.
 *
 * Trigger ausschliesslich manuell (Klick auf den `?`-Button in der
 * Kalender-Toolbar). State-Policy erlaubt beliebiges Replay (Hilfe-Modus).
 *
 * Datenabhaengige bzw. permission-gated Schritte (Wochenliste, offene
 * Anforderungen, Drei-Punkte-Menue, Freigeben) skippen ueber `skipIf` per
 * DOM-Existenz-Check, damit die Tour auch in schlanken Konten ohne diese
 * Elemente sauber durchlaeuft.
 */
export const SHIFT_ASSIGNMENT_HELP_TOUR: TourConfig = {
  id: TourFeature.ShiftAssignmentHelp,
  version: 1,
  title: 'TUTORIAL.SHIFT_ASSIGNMENT.TITLE',
  description: 'TUTORIAL.SHIFT_ASSIGNMENT.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TUTORIAL.SHIFT_ASSIGNMENT.TITLE',
      steps: [
        {
          // 1) Worum geht's? Schritt 2 des Planungs-Workflows.
          id: 'sa-welcome',
          anchor: AnchorId.PlannerView,
          spotlightAnchor: AnchorId.PlannerView,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.WELCOME.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.WELCOME.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // 2) Wochenliste links — Abdeckung je KW auf einen Blick.
          id: 'sa-week-list',
          anchor: AnchorId.CalendarWeekList,
          spotlightAnchor: AnchorId.CalendarWeekList,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.WEEK_LIST.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.WEEK_LIST.CONTENT',
          placement: 'right',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="calendar-week-list"]'),
        },
        {
          // 3) Mitarbeiter suchen + filtern.
          id: 'sa-search-filter',
          anchor: AnchorId.EmployeeSearchInput,
          spotlightAnchor: AnchorId.EmployeeSearchInput,
          additionalSpotlightAnchors: [AnchorId.FilterButton],
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.SEARCH_FILTER.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.SEARCH_FILTER.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="employee-search-input"]'),
        },
        {
          // 4) Mitarbeiterspalte: Abteilungs-Gruppen + Stunden-Ring.
          id: 'sa-resources',
          anchor: AnchorId.EmployeeResourceList,
          spotlightAnchor: AnchorId.EmployeeResourceList,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.RESOURCES.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.RESOURCES.CONTENT',
          placement: 'right',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="employee-resource-list"]'),
        },
        {
          // 5) Offene Anforderungen — offener Bedarf je Tag (besetzt/benoetigt).
          id: 'sa-open-requirements',
          anchor: AnchorId.OpenRequirementsRow,
          spotlightAnchor: AnchorId.OpenRequirementsRow,
          additionalSpotlightAnchors: [AnchorId.OpenRequirementsLane],
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.OPEN_REQUIREMENTS.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.OPEN_REQUIREMENTS.CONTENT',
          placement: 'right',
          showNextButton: true,
          // FullCalendar baut die Resource-Zeilen bei jedem Re-Render neu auf
          // und verwirft dabei die programmatisch gesetzten onboardingAnchor-
          // Attribute. Darum den Anker hier direkt vor dem Step (re-)setzen,
          // statt auf das Attribut zu skippen — und das Ueberspringen an der
          // tatsaechlichen Existenz der „Offene Anforderungen"-Zeile festmachen.
          skipIf: () => !document.querySelector(OPEN_REQ_ROW_SELECTOR),
          onBeforeShow: () => reattachOpenRequirementsAnchors(),
        },
        {
          // 6) Kalender-Raster: Zellen = Tage, Kacheln = Schichten, Klick = neu.
          id: 'sa-calendar-grid',
          anchor: AnchorId.CalendarCellArea,
          spotlightAnchor: AnchorId.CalendarGrid,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.CALENDAR.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.CALENDAR.CONTENT',
          placement: 'top',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="calendar-cell-area"]') &&
            !document.querySelector('[onboardingAnchor="calendar-grid"]'),
        },
        {
          // 7) Primaere Toolbar-Aktionen.
          id: 'sa-toolbar',
          anchor: AnchorId.ActionButtons,
          spotlightAnchor: AnchorId.ActionButtons,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.TOOLBAR.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.TOOLBAR.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="action-buttons"]'),
          // Falls der User aus dem naechsten (Overflow-)Step zurueckspringt,
          // das ggf. noch offene Menue schliessen. Der TourCoordinator ruft
          // `onAfterHide` beim Weiterschalten NICHT verlaesslich auf, deshalb
          // schliesst der Nachbar-Step das Menue (Muster aus employee-list-help).
          onBeforeShow: () => plannerTourBridge.closeOverflowMenu(),
        },
        {
          // 8) Drei-Punkte-Menue (⋮): Analyse · Exportieren · Wochenvorlagen ·
          //    Vorwoche kopieren · Loeschen. onBeforeShow oeffnet das echte
          //    MatMenu ueber die plannerTourBridge und haengt den Spotlight-
          //    Anker an das geoeffnete Panel. Geschlossen wird es vom jeweils
          //    folgenden Step (Toolbar zurueck / Publish vor), da der
          //    TourCoordinator `onAfterHide` beim Weiterschalten nicht ruft.
          id: 'sa-overflow-menu',
          // Popover am geoeffneten Menue-Panel verankern und LINKS davon
          // platzieren, damit es das Menue nicht verdeckt (es haengt rechts
          // oben in der Toolbar, links davon ist Platz im Kalender).
          anchor: AnchorId.ShiftOverflowMenu,
          spotlightAnchor: AnchorId.ShiftOverflowMenu,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.OVERFLOW_MENU.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.OVERFLOW_MENU.CONTENT',
          placement: 'left',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="shift-overflow-button"]'),
          onBeforeShow: () =>
            new Promise<void>((resolve) => {
              // Echtes Overflow-MatMenu ueber die Komponenten-Bruecke oeffnen.
              plannerTourBridge.openOverflowMenu();
              // Zwei Frames warten, bis das MatMenu-Panel im CDK-Overlay liegt,
              // dann den Spotlight-Anker daran haengen.
              requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                  document
                    .querySelector('.planner-overflow-menu')
                    ?.setAttribute('onboardingAnchor', 'shift-overflow-menu');
                  resolve();
                })
              );
            }),
          // Menue wieder schliessen, sobald der Step verlassen wird (sauberer
          // MatMenuTrigger-Close ueber die Bruecke statt Backdrop-Hack).
          onAfterHide: () => plannerTourBridge.closeOverflowMenu(),
        },
        {
          // 9) An Mitarbeiter freigeben — macht den Plan sichtbar + benachrichtigt.
          id: 'sa-publish',
          anchor: AnchorId.PublishButton,
          spotlightAnchor: AnchorId.PublishButton,
          title: 'TUTORIAL.SHIFT_ASSIGNMENT.PUBLISH.TITLE',
          content: 'TUTORIAL.SHIFT_ASSIGNMENT.PUBLISH.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="publish-button"]'),
          // Overflow-Menue des vorigen Steps zuverlaessig schliessen (Vorwaerts-
          // Weg). Harmlos, wenn es nicht offen ist.
          onBeforeShow: () => plannerTourBridge.closeOverflowMenu(),
        },
      ],
    },
  ],
};
